import { Paper, Text, Timeline } from "@mantine/core"
import { ProhibitIcon, CheckIcon, EyeIcon, FilePlusIcon, XIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { IOrderPlus } from "../../models/Order"
import { dateTimeSecFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"

type Props = {
  data: IOrderPlus
}

type ActivityItem = {
  date: string
  title: string
  description: string
  icon: React.ReactNode
  color: string
}

export function ActivityLog({ data }: Props) {
  const { t } = useTranslation(["order", "common"])
  const activities: ActivityItem[] = []

  if (data.createdAt) {
    activities.push({
      date: data.createdAt,
      title: t("order:purchaseSales.activityCreated"),
      description: `${renderName(data.orderedUser)} - ${dateTimeSecFormat(data.createdAt)}`,
      icon: <FilePlusIcon size={14} />,
      color: "blue",
    })
  }

  if (data.reviewedDate) {
    activities.push({
      date: data.reviewedDate,
      title: t("order:purchaseSales.activityReviewed"),
      description: `${renderName(data.reviewedUser)} - ${dateTimeSecFormat(data.reviewedDate)}`,
      icon: <EyeIcon size={14} />,
      color: "cyan",
    })
  }

  if (data.respondedDate && data.orderStatus !== "REJECTED") {
    activities.push({
      date: data.respondedDate,
      title: t("order:purchaseSales.activityApproved"),
      description: `${renderName(data.respondedUser)} - ${dateTimeSecFormat(data.respondedDate)}`,
      icon: <CheckIcon size={14} />,
      color: "green",
    })
  }

  if (data.respondedDate && data.orderStatus === "REJECTED") {
    activities.push({
      date: data.respondedDate,
      title: t("order:purchaseSales.activityDeclined"),
      description: `${renderName(data.respondedUser)} - ${dateTimeSecFormat(data.respondedDate)}`,
      icon: <XIcon size={14} />,
      color: "red",
    })
  }

  if (data.canceledDate) {
    activities.push({
      date: data.canceledDate,
      title: t("order:purchaseSales.activityCanceled"),
      description: dateTimeSecFormat(data.canceledDate),
      icon: <ProhibitIcon size={14} />,
      color: "red",
    })
  }

  activities.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  if (activities.length === 0) {
    return (
      <Text c="gray.8" fz={14}>
        {t("order:purchaseSales.noHistory")}
      </Text>
    )
  }

  return (
    <Paper p="md" withBorder radius="md">
      <Timeline active={activities.length - 1} bulletSize={28} lineWidth={2}>
        {activities.map((item, i) => (
          <Timeline.Item key={i} bullet={item.icon} color={item.color} title={item.title}>
            <Text c="gray.8" fz={14}>
              {item.description}
            </Text>
          </Timeline.Item>
        ))}
      </Timeline>
    </Paper>
  )
}
